/**
 * Derive a dark + light + accent palette from any base hex color.
 *
 * The Login dashboard uses this to compute the cohesive 3-tone scheme that
 * paints the left hero panel, headline buttons and CTA badges. Caller is
 * responsible for picking the base — typically the SuperAdmin's
 * `theme_default_light` / `theme_default_dark` for the active mode.
 *
 * Falls back to the classic teal from the reference design when the input
 * is invalid or missing, so unconfigured deployments still look good.
 *
 * @param {string} [baseHex] - Optional 6-digit hex (with or without '#').
 *   When omitted we fall back to legacy behaviour (read localStorage) so
 *   older callers continue to work.
 */

function hexToRgb(hex) {
  const s = (hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return null;
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  const c = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function mix(rgb, target, t) {
  return {
    r: rgb.r + (target.r - rgb.r) * t,
    g: rgb.g + (target.g - rgb.g) * t,
    b: rgb.b + (target.b - rgb.b) * t,
  };
}

/**
 * Returns { dark, light, accent } derived from a base color.
 */
export function deriveBrandColors(baseHex) {
  const fromArg = hexToRgb(baseHex);
  const fromStorage = !fromArg && typeof window !== 'undefined'
    ? hexToRgb(localStorage.getItem('app.theme_color') || '')
    : null;
  const base = fromArg || fromStorage || hexToRgb('#0d4d47') || { r: 13, g: 77, b: 71 };
  const dark = mix(base, { r: 0, g: 0, b: 0 }, 0.25); // darken 25% toward black
  const light = mix(base, { r: 255, g: 255, b: 255 }, 0.88); // lighten 88% toward white
  return {
    dark: rgbToHex(dark.r, dark.g, dark.b),
    light: rgbToHex(light.r, light.g, light.b),
    accent: rgbToHex(base.r, base.g, base.b),
  };
}
